import React, { useState, useEffect } from 'react';
import { SubscriptionPlan, ChargeRequest } from '../../types/billing.types';
import { COLORS } from '../../../../core/constants/colors';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: SubscriptionPlan | null;
  onCharge: (chargeData: ChargeRequest) => Promise<void>;
  isLoading?: boolean;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  plan,
  onCharge,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<ChargeRequest>({
    card_details: {
      card_number: '',
      cvv: '',
      expiry_month: '',
      expiry_year: '',
    },
    channel: 'Cards',
    country: 'KEN',
    first_name: '',
    last_name: '',
    phone: '',
    plan_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (isOpen && plan) {
      setFormData((prev) => ({
        ...prev,
        plan_id: plan.plan_id,
      }));
    }
  }, [isOpen, plan]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        card_details: {
          card_number: '',
          cvv: '',
          expiry_month: '',
          expiry_year: '',
        },
        channel: 'Cards',
        country: 'KEN',
        first_name: '',
        last_name: '',
        phone: '',
        plan_id: plan?.plan_id || '',
      });
      setErrors({});
      setHasSubmitted(false);
      setApiError('');
    }
  }, [isOpen, plan]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    const cardNumber = formData.card_details.card_number.replace(/\s/g, '');
    if (!cardNumber) {
      newErrors.card_number = 'Card number is required';
    } else if (!/^\d{13,19}$/.test(cardNumber)) {
      newErrors.card_number = 'Please enter a valid card number';
    }

    if (!formData.card_details.cvv) {
      newErrors.cvv = 'CVV is required';
    } else if (!/^\d{3,4}$/.test(formData.card_details.cvv)) {
      newErrors.cvv = 'Please enter a valid CVV';
    }

    if (!formData.card_details.expiry_month) {
      newErrors.expiry_month = 'Expiry month is required';
    } else if (!/^(0[1-9]|1[0-2])$/.test(formData.card_details.expiry_month)) {
      newErrors.expiry_month = 'Please enter a valid month (01-12)';
    }

    if (!formData.card_details.expiry_year) {
      newErrors.expiry_year = 'Expiry year is required';
    } else if (!/^\d{4}$/.test(formData.card_details.expiry_year)) {
      newErrors.expiry_year = 'Please enter a valid year (YYYY)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    setApiError('');

    if (!validateForm()) {
      return;
    }

    try {
      // Remove spaces from card number before sending
      const chargeData = {
        ...formData,
        card_details: {
          ...formData.card_details,
          card_number: formData.card_details.card_number.replace(/\s/g, ''),
        },
      };
      await onCharge(chargeData);
    } catch (error: any) {
      console.error('Payment error:', error);
      setApiError(error.message || 'Failed to process payment');
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.substring(0, 19);
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData((prev) => ({
      ...prev,
      card_details: {
        ...prev.card_details,
        card_number: formatted,
      },
    }));
  };

  const formatAmount = (amount: string, currency: string) => {
    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return amount;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numAmount);
    } catch {
      return `${currency} ${amount}`;
    }
  };

  if (!isOpen || !plan) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Side Drawer */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} style={{ maxHeight: '100vh', overflowY: 'auto' }}>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              Complete Payment
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              disabled={isLoading}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {/* Error Message */}
            {apiError && (
              <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600">{apiError}</p>
              </div>
            )}

            {/* Plan Summary */}
            <div className="mb-6 p-4 rounded-lg border" style={{ borderColor: '#E5E7EB', backgroundColor: '#F9FAFB' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Plan</p>
                  <p className="text-base font-semibold text-gray-900">{plan.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-500 mb-1">Amount</p>
                  <p className="text-base font-semibold" style={{ color: COLORS.user.primary }}>
                    {formatAmount(plan.amount, plan.currency)}
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, first_name: e.target.value }))
                    }
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.first_name ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.first_name ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                  />
                  {hasSubmitted && errors.first_name && (
                    <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, last_name: e.target.value }))
                    }
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.last_name ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.last_name ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                  />
                  {hasSubmitted && errors.last_name && (
                    <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  placeholder="+2348012345678"
                  className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    hasSubmitted && errors.phone ? 'border-red-500' : ''
                  }`}
                  style={{ borderColor: hasSubmitted && errors.phone ? '#DC2626' : '#E5E7EB' }}
                  disabled={isLoading}
                />
                {hasSubmitted && errors.phone && (
                  <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Card Number *
                </label>
                <input
                  type="text"
                  value={formData.card_details.card_number}
                  onChange={handleCardNumberChange}
                  placeholder="5123 4500 0000 0008"
                  maxLength={19}
                  className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    hasSubmitted && errors.card_number ? 'border-red-500' : ''
                  }`}
                  style={{ borderColor: hasSubmitted && errors.card_number ? '#DC2626' : '#E5E7EB' }}
                  disabled={isLoading}
                />
                {hasSubmitted && errors.card_number && (
                  <p className="text-xs text-red-500 mt-1">{errors.card_number}</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Expiry Month *
                  </label>
                  <input
                    type="text"
                    value={formData.card_details.expiry_month}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        card_details: {
                          ...prev.card_details,
                          expiry_month: e.target.value,
                        },
                      }))
                    }
                    placeholder="06"
                    maxLength={2}
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.expiry_month ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.expiry_month ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                  />
                  {hasSubmitted && errors.expiry_month && (
                    <p className="text-xs text-red-500 mt-1">{errors.expiry_month}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Expiry Year *
                  </label>
                  <input
                    type="text"
                    value={formData.card_details.expiry_year}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        card_details: {
                          ...prev.card_details,
                          expiry_year: e.target.value,
                        },
                      }))
                    }
                    placeholder="2028"
                    maxLength={4}
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.expiry_year ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.expiry_year ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                  />
                  {hasSubmitted && errors.expiry_year && (
                    <p className="text-xs text-red-500 mt-1">{errors.expiry_year}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    CVV *
                  </label>
                  <input
                    type="text"
                    value={formData.card_details.cvv}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        card_details: {
                          ...prev.card_details,
                          cvv: e.target.value.replace(/\D/g, ''),
                        },
                      }))
                    }
                    placeholder="100"
                    maxLength={4}
                    className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      hasSubmitted && errors.cvv ? 'border-red-500' : ''
                    }`}
                    style={{ borderColor: hasSubmitted && errors.cvv ? '#DC2626' : '#E5E7EB' }}
                    disabled={isLoading}
                  />
                  {hasSubmitted && errors.cvv && (
                    <p className="text-xs text-red-500 mt-1">{errors.cvv}</p>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-5 border-t flex gap-4 flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg border font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              style={{ borderColor: '#E5E7EB' }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: COLORS.user.primary }}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Pay Now'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
